import authStorage from '../utils/authStorage';
import axios from '../utils/axios';
import { loginSuccess, logout } from '../features/common/authSlice';
import store from './store';

const HANDSHAKE_KEY = 'bbsCmsHandshake';
const EXCHANGE_PATH = '/public_v1/bbs-cms/sso/exchange';

const inflight = new Map();
let initialized = false;
let memoryHandshake = null; // { token, origin } — in-memory only, used when crypto.subtle is unavailable
let lastCmsOrigin = null; // exact origin that established (or attempted) the iframe handshake
let sessionExpiredNotified = false; // at most one BBS_SEO_SESSION_EXPIRED per session attempt
let lastAttemptedToken = null; // token hash of the latest SSO_AUTH, to detect a new attempt

const sha256Hex = async (text) => {
  try {
    if (!window?.crypto?.subtle) return null;
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    return null;
  }
};

const getHandshake = () => authStorage.getJSON(HANDSHAKE_KEY, null);
const setHandshake = (tokenHash, origin) => authStorage.setJSON(HANDSHAKE_KEY, { tokenHash, origin });
const clearHandshake = () => authStorage.remove(HANDSHAKE_KEY);

const isValidOrigin = (origin) => typeof origin === 'string' && origin !== '' && origin !== 'null';

const rememberCmsOrigin = (origin) => {
  if (isValidOrigin(origin)) {
    lastCmsOrigin = origin;
  }
};

export const notifyBbsCmsSessionExpired = () => {
  if (typeof window === 'undefined' || window.self === window.top) return;
  if (sessionExpiredNotified) return;

  const origin = lastCmsOrigin || getHandshake()?.origin || memoryHandshake?.origin;
  if (!isValidOrigin(origin)) return;

  sessionExpiredNotified = true;
  window.parent.postMessage({ type: 'BBS_SEO_SESSION_EXPIRED' }, origin);
};

const postToParent = (message, origin) => {
  if (window.parent && isValidOrigin(origin)) {
    window.parent.postMessage(message, origin);
  }
};

const TERMINAL_HANDSHAKE_REASONS = new Set([
  'expired',
  'already_used',
  'invalid',
  'origin_mismatch',
  'not_bbs_cms_session',
]);

const postSsoError = (reason, origin) => {
  postToParent({ type: 'SSO_ERROR', reason }, origin);
  if (TERMINAL_HANDSHAKE_REASONS.has(reason)) {
    notifyBbsCmsSessionExpired();
  }
};

const mapExchangeError = (error) => {
  const status = error?.response?.status;
  if (status === 429) return 'throttled';

  const reason = error?.response?.data?.reason;
  if (reason) return reason;

  if (status === 422) return 'invalid';

  return 'network';
};

const describeError = (error) => {
  if (!error) return 'unknown';
  if (error.response) return `HTTP ${error.response.status}`;
  if (error.code) return `${error.code}: ${error.message}`;
  return error.message || 'network error';
};

const revokeSessionToken = (token) => {
  if (!token) return;
  axios.post('/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
};

const fetchUserInfoWithRetry = async (token) => {
  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await axios.get('/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      lastError = error;
      if (attempt === 2) break;
      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
    }
  }

  throw lastError;
};

const exchange = async (token, origin, tokenHash) => {
  let response;
  try {
    response = await axios.post(EXCHANGE_PATH, { sso_token: token, origin });
  } catch (error) {
    postSsoError(mapExchangeError(error), origin);
    return;
  }

  const newToken = response?.data?.data?.token;

  if (!newToken) {
    postSsoError('invalid', origin);
    return;
  }

  const previousToken = authStorage.get('token');
  if (previousToken && previousToken !== newToken) {
    revokeSessionToken(previousToken); // best-effort; bounds accumulated session tokens
  }

  authStorage.set('token', newToken);
  if (tokenHash) {
    setHandshake(tokenHash, origin);
  }
  memoryHandshake = { token, origin };
  authStorage.remove('is_super_login');
  axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

  let userInfo;
  try {
    userInfo = await fetchUserInfoWithRetry(newToken);
  } catch (error) {
    console.warn('[bbsCmsSso] /api/user failed after exchange:', describeError(error), error);
    clearHandshake();
    memoryHandshake = null;
    authStorage.remove('token');
    delete axios.defaults.headers.common.Authorization;
    postSsoError('network', origin);
    return;
  }

  if (userInfo?.service_access?.bbsCmsUser !== true) {
    clearHandshake();
    memoryHandshake = null;
    authStorage.remove('token');
    authStorage.remove('serviceAccess');
    authStorage.remove('is_gsc_optional');
    delete axios.defaults.headers.common.Authorization;
    postSsoError('not_bbs_cms_session', origin);
    return;
  }

  const { token: _ignoredToken, service_access, ...rest } = userInfo;

  if (rest?.roles) {
    authStorage.setJSON('roles', rest.roles);
  }

  store.dispatch(loginSuccess({ token: newToken, service_access, userInfo: rest }));

  rememberCmsOrigin(origin);
  sessionExpiredNotified = false;

  postToParent({ type: 'SSO_AUTHENTICATED' }, origin);
};

const handleSsoAuth = async (event) => {
  const data = event?.data;

  if (!data || data.type !== 'SSO_AUTH') return;
  if (typeof data.token !== 'string' || data.token.length < 32 || data.token.length > 256) return;
  if (event.source !== window.parent) return;
  if (!isValidOrigin(event.origin)) return;

  rememberCmsOrigin(event.origin);

  const token = data.token;
  const tokenHash = await sha256Hex(token);
  const dedupeKey = tokenHash || token;

  if (lastAttemptedToken !== dedupeKey) {
    lastAttemptedToken = dedupeKey;
    sessionExpiredNotified = false;
  }

  const sessionToken = authStorage.get('token');
  const handshake = getHandshake();

  const matchesPersisted =
    sessionToken && handshake && tokenHash && handshake.tokenHash === tokenHash && handshake.origin === event.origin;
  const matchesMemory =
    sessionToken && memoryHandshake && memoryHandshake.token === token && memoryHandshake.origin === event.origin;

  if (matchesPersisted || matchesMemory) {
    rememberCmsOrigin(event.origin);
    sessionExpiredNotified = false;
    postToParent({ type: 'SSO_AUTHENTICATED' }, event.origin);
    return;
  }

  if (inflight.has(dedupeKey)) {
    await inflight.get(dedupeKey);
    return;
  }

  const promise = exchange(token, event.origin, tokenHash).finally(() => {
    inflight.delete(dedupeKey);
  });

  inflight.set(dedupeKey, promise);
  await promise;
};

const handleSsoLogout = (event) => {
  const data = event?.data;

  if (!data || data.type !== 'SSO_LOGOUT') return;
  if (event.source !== window.parent) return;
  if (!isValidOrigin(event.origin)) return;

  const handshake = getHandshake();
  const boundOrigin = handshake?.origin || memoryHandshake?.origin;
  if (boundOrigin && boundOrigin !== event.origin) return;

  revokeSessionToken(authStorage.get('token'));
  clearHandshake();
  memoryHandshake = null;
  sessionExpiredNotified = true;
  store.dispatch(logout());
};

export const initBbsCmsSso = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Seed the CMS origin from a persisted handshake so a session that expires
  // after a reload can still notify the correct parent origin.
  const persistedHandshake = getHandshake();
  if (persistedHandshake?.origin) {
    rememberCmsOrigin(persistedHandshake.origin);
  }

  window.addEventListener('message', (event) => {
    const data = event?.data;
    if (!data || typeof data !== 'object') return;

    if (data.type === 'SSO_AUTH') {
      return handleSsoAuth(event);
    }
    if (data.type === 'SSO_LOGOUT') {
      return handleSsoLogout(event);
    }

    return undefined;
  });

  window.addEventListener('bbs-cms:sso-session-lost', () => {
    notifyBbsCmsSessionExpired();
    clearHandshake();
    memoryHandshake = null;
  });
};
